/**
 * baleProgress — derived bale-session rules for the live scoring page.
 *
 * These are the business rules that used to sit inline in Score.jsx (#2061):
 * "did MY round actually commit?", "are my bale mates still behind me?" and
 * "what role does this device play in the session?". They are pure functions of
 * the bale-session payload + the local round, with no React dependency, so the
 * page and the scoring-session hook can share one definition.
 */

/**
 * #1903 — did the bulk confirm commit OUR round? A 200 from confirm-end is
 * not proof: the backend commits only rounds at min_ends (by design), so a
 * device ahead of its bale mates gets the session back unchanged. Prefer the
 * explicit committed_round_ids field on the confirm response; fall back to
 * comparing our ends_completed against the local ends_done for older servers.
 * Defaults to true when the response carries neither signal (pre-#1903 shape).
 */
export function ownRoundCommitted(detail, roundId, localEndsDone) {
  if (Array.isArray(detail?.committed_round_ids)) {
    return detail.committed_round_ids.includes(roundId);
  }
  const entry = (detail?.archers || []).find((a) => a.round_id === roundId);
  return entry ? entry.ends_completed > localEndsDone : true;
}

/**
 * #1903 — is any ACTIVE bale mate still behind this round's ends_done?
 *
 * Drives the auto-clear of the "waiting for the other archers" banner: once no
 * active mate is still behind our local ends_done, the wait is over and the
 * next Confirm end will commit. Early-ended / complete mates never gate
 * min_ends, so they are excluded.
 */
export function baleMatesStillBehind(baleSession, round) {
  if (!baleSession || !round) return false;
  return (baleSession.archers || []).some(
    (a) =>
      a.round_id !== round.id &&
      a.ends_completed < a.ends_total && // early-ended/complete mates don't gate min_ends
      a.ends_completed < round.ends_done,
  );
}

/**
 * Scorer/viewer role derivation for a bale session.
 *
 *   isScorerMode — the session has an assigned scorer at all
 *   isScorer     — this user IS that scorer (writes, broadcasts their view)
 *   isViewer     — logged in, scorer mode active, but not the scorer (read-only)
 */
export function scoringRoles(baleSession, user) {
  const isScorerMode = Boolean(baleSession?.scorer_user_id);
  const isScorer = isScorerMode && user?.id === baleSession?.scorer_user_id;
  return { isScorerMode, isScorer, isViewer: isScorerMode && !isScorer };
}
